import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, GridColumn, Icon, Label, Menu, MenuItem, Popup } from "semantic-ui-react";

import { Pin, getPinColor, useGetPinOptionsDropdownOptions } from "components";
import { useShowPriceInVat } from "hooks";
import { QuestionResultStatusSelector } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components";
import { CustomerCommunicationAnswer, ImportantNotes } from "modules/AppointmentDetails/components/Interventions/components";
import "modules/AppointmentDetails/components/Interventions/Interventions.scss";
import {
  setConnectedInterventionIdToFilter,
  setConnectedQuestionResultIdToFilter,
  useAppointmentDealer,
  useCarBrandsQuery,
  useConnectedQuestionResultIdToFilter
} from "modules/AppointmentDetails/hooks";
import { Attachments, GeneralInfo, InterventionChecklists, PinCategory, Subitems } from "modules/HoldingAppointmentDetails/components/Interventions/components";
import { getInterventionPriceWithVAT } from "utils/common";
import { formatPrice } from "utils/formatPrice";

import type { Appointment, CarCheck, CommunicationEvent, DealerLocation, Intervention as InterventionModel, PIN_TYPES } from "models";

type InterventionItemProps = {
  activeIntervention: number | null;
  appointment: Appointment;
  appointmentLocation: DealerLocation;
  index: number;
  intervention: InterventionModel;
  checks?: CarCheck[];
  diagnoseOverviewEvent?: CommunicationEvent;
  onRequestCloseTabs: (index: number | null) => void;
};

export enum INTERVENTION_TABS {
  General = "general",
  Subitems = "subitems",
  PinType = "pintype",
  Attachments = "attachments",
  Checklists = "checklists"
}

export const Intervention = ({
  appointment,
  appointmentLocation,
  checks,
  intervention,
  index,
  onRequestCloseTabs,
  activeIntervention,
  diagnoseOverviewEvent
}: InterventionItemProps) => {
  const { t } = useTranslation();

  const latestPin = intervention.pin_history?.at(0);

  const [showTabs, setShowTabs] = useState(false);
  const [activeTab, setActiveTab] = useState<INTERVENTION_TABS | null>(INTERVENTION_TABS.General);
  const [pinTypeID, setPinTypeID] = useState<PIN_TYPES | null>(latestPin?.pin_type_id ?? null);

  const showPriceInVat = useShowPriceInVat();
  const appointmentDealer = useAppointmentDealer(appointment);
  const { pinTypeOptions } = useGetPinOptionsDropdownOptions(appointmentDealer);
  const { data: brands = [] } = useCarBrandsQuery();
  const connectedQuestionResultId = useConnectedQuestionResultIdToFilter();

  const connectedChecks = checks?.filter(checklist => checklist.intervention_id === intervention.id) || [];
  const pendingChecklistsCount = intervention.pending_checklists?.length ?? 0;
  const checksAndPendginChecklistsCount = connectedChecks.length + pendingChecklistsCount;
  const hasImportantNotes = Boolean(intervention.pin_history?.some(element => element.visible_important_items));
  const selectedPinTypeOption = pinTypeOptions.find(option => option.value === pinTypeID);
  const attachments = [...(intervention.attachments || []), ...(intervention.diagnose_overview_attachments || [])];

  useEffect(() => {
    return () => setConnectedQuestionResultIdToFilter(null);
  }, []);

  useEffect(() => {
    if (activeTab === INTERVENTION_TABS.Checklists) {
      setConnectedInterventionIdToFilter(intervention.id);
      return;
    }
    setConnectedInterventionIdToFilter(null);
  }, [activeTab, showTabs]);

  useEffect(() => {
    setPinTypeID(latestPin?.pin_type_id ?? null);
  }, [latestPin?.pin_type_id]);

  useEffect(() => {
    if (activeIntervention === index) {
      if (activeTab === null) setActiveTab(INTERVENTION_TABS.General);
      setShowTabs(true);
    } else {
      setActiveTab(null);
      setShowTabs(false);
    }
  }, [activeIntervention]);

  const toggleTabs = () => onRequestCloseTabs(activeIntervention === index ? null : index);

  const handleTab = (tab: INTERVENTION_TABS) => setActiveTab(tab);

  const handleSubTab = (evt: React.MouseEvent<HTMLElement>, name: INTERVENTION_TABS) => {
    evt.preventDefault();
    evt.stopPropagation();
    setShowTabs(activeTab === name ? false : true);
    setActiveTab(activeTab === name ? null : name);
    onRequestCloseTabs(index);
  };

  const resetConnectedQuestionResultIdOnInterventionClose = () => {
    if ((!showTabs && connectedQuestionResultId !== intervention.question_result_id) || (showTabs && connectedQuestionResultId === intervention.question_result_id))
      setConnectedQuestionResultIdToFilter(null);
  };

  return (
    <div className="Intervention" onClick={resetConnectedQuestionResultIdOnInterventionClose}>
      <Grid className="intervention-main" onClick={toggleTabs}>
        <GridColumn width={8} className="intervention-container">
          <div className="title-container">
            {intervention.check_in_result_id && (
              <div className="question-status-container">
                <div className="view-only">
                  <Icon className="cart icon-container grey" />
                </div>
              </div>
            )}
            {(intervention.check_in_remark_id || intervention.diagnose_overview_remark_id) && (
              <div className="question-status-container">
                <div className="view-only">
                  <Icon className="note sticky icon-container blue" />
                </div>
              </div>
            )}
            {intervention.kiosk_remark_id && (
              <div className="question-status-container">
                <div className="view-only">
                  <Icon className="note sticky icon-container green" />
                </div>
              </div>
            )}
            {intervention.keylocker_remark_id && (
              <div className="question-status-container">
                <div className="view-only">
                  <Icon className="note sticky icon-container purple" />
                </div>
              </div>
            )}
            {intervention.question_result_id && (
              <div className={`question-status-container  ${connectedQuestionResultId === intervention.question_result_id ? "active-connected-checklist-item" : ""}`}>
                <QuestionResultStatusSelector intervention={intervention} disabled />
              </div>
            )}
            <div className="title-container-button">
              {!intervention.is_local && <Icon className="desktop" size="tiny" />}
              <div className="title-container-intervention-text">
                <div className="title-container-intervention-title-container">
                  <p className="ellipsis title-container-intervention-title">{intervention.title}</p>
                </div>
                {intervention.description.trim() && <span className="ellipsis title-container-intervention-description">{intervention.description.trim()}</span>}
              </div>
              <Icon className={`chevron ${showTabs ? "down" : "right"}`} size="small" />
            </div>
          </div>
        </GridColumn>

        <GridColumn width={2} className="intervention-container">
          {appointmentLocation.dms_price_enabled && (
            <div className="column-container float-right">
              <p>{`€ ${showPriceInVat ? formatPrice(getInterventionPriceWithVAT(intervention, appointmentLocation)) : formatPrice(intervention.price)}`}</p>
            </div>
          )}
        </GridColumn>

        <GridColumn width={6} className="intervention-container">
          <div className="column-container float-right">
            <div className="column-container gap">
              {(connectedChecks.length > 0 || pendingChecklistsCount > 0) && (
                <Label className={`checklist-label ${pendingChecklistsCount > 0 ? "red" : ""}`} onClick={e => handleSubTab(e, INTERVENTION_TABS.Checklists)}>
                  <div className="flex-row">
                    <Icon className="standard list" /> {checksAndPendginChecklistsCount}
                  </div>
                </Label>
              )}
              {!!intervention.elements?.length && (
                <Label className="green" onClick={e => handleSubTab(e, INTERVENTION_TABS.Subitems)}>
                  <div className="flex-row">
                    <Icon className="diagram subtask" />
                    {intervention.elements.length}
                  </div>
                </Label>
              )}

              {attachments.length > 0 && (
                <Label className="skyblue" onClick={e => handleSubTab(e, INTERVENTION_TABS.Attachments)}>
                  <div className="flex-row">
                    <Icon className="paperclip" /> {attachments.length}
                  </div>
                </Label>
              )}
              {latestPin && selectedPinTypeOption && (
                <Pin
                  pin={{ ...latestPin, history: intervention.pin_history ?? [] }}
                  location={appointmentLocation}
                  brands={brands}
                  vin={appointment.vin_nr}
                  intervention={intervention}
                  onClick={e => handleSubTab(e, INTERVENTION_TABS.PinType)}
                  readOnly
                />
              )}
            </div>
            <CustomerCommunicationAnswer
              interventionIsDeclinedByCustomer={intervention.is_declined_by_customer}
              communicationResultStatus={diagnoseOverviewEvent?.intervention_results?.find(r => r.intervention_id === intervention.id)?.communication_result_status}
            />
            <div className="status-container">
              <div className="action-icon-container">
                <Icon className={`eye ${intervention.visible_to_customer ? "green" : "slash red"}`} size="small" />
              </div>
              <div className="action-icon-container">
                <Icon className={`${intervention.customer_ok ? "check circle green" : "remove circle red"}`} size="large" disabled />
              </div>
              <div className="position-relative action-icon-container">
                {intervention.mechanic_fixed && (
                  <Popup
                    content={intervention.last_mechanic_name}
                    disabled={!intervention.last_mechanic_name}
                    trigger={
                      <div>
                        <Icon className="wrench green" size="small" />
                        <Icon className="check circle green wrench-check-icon" />
                      </div>
                    }
                  />
                )}
                {!intervention.mechanic_fixed && intervention.customer_ok && <Icon className="wrench solid not-fixed-wrench rotate-270" size="small" />}
                {!intervention.mechanic_fixed && !intervention.customer_ok && <Icon className="wrench regular not-fixed-wrench rotate-270 grey" size="small" />}
              </div>
            </div>
          </div>
        </GridColumn>

        {!showTabs && hasImportantNotes && (
          <GridColumn width={16} style={{ padding: 0 }}>
            <ImportantNotes data={intervention.pin_history} />
          </GridColumn>
        )}
      </Grid>

      {showTabs && (
        <div className="tabs">
          <Menu tabular>
            <MenuItem name={INTERVENTION_TABS.General} active={activeTab === INTERVENTION_TABS.General} onClick={() => handleTab(INTERVENTION_TABS.General)}>
              <div className={`tab green ${activeTab === "general" ? "active" : ""}`}>
                <Icon className="screwdriver wrench" />
                <p>{t("v8_general_info", "General Info")}</p>
              </div>
            </MenuItem>
            <MenuItem name={INTERVENTION_TABS.Subitems} active={activeTab === INTERVENTION_TABS.Subitems} onClick={() => handleTab(INTERVENTION_TABS.Subitems)}>
              <div className={`tab darkgreen ${activeTab === INTERVENTION_TABS.Subitems ? "active" : ""}`}>
                <Icon className="diagram subtask" />
                <p>
                  {t("v8_subitems", "Subitems")} {intervention.elements?.length ? `(${intervention.elements.length})` : null}
                </p>
              </div>
            </MenuItem>
            {latestPin && selectedPinTypeOption && (
              <MenuItem name={INTERVENTION_TABS.PinType} active={activeTab === INTERVENTION_TABS.PinType} onClick={() => handleTab(INTERVENTION_TABS.PinType)}>
                <div
                  className={`tab ${getPinColor({ pin: latestPin, vin: appointment.vin_nr, location: appointmentLocation, brands, intervention })} ${activeTab === INTERVENTION_TABS.PinType ? "active" : ""}`}
                >
                  {selectedPinTypeOption.icon}
                  <p>{selectedPinTypeOption.text}</p>
                </div>
              </MenuItem>
            )}
            <MenuItem name={INTERVENTION_TABS.Attachments} active={activeTab === INTERVENTION_TABS.Attachments} onClick={() => handleTab(INTERVENTION_TABS.Attachments)}>
              <div className={`tab skyblue ${activeTab === INTERVENTION_TABS.Attachments ? "active" : ""}`}>
                <Icon className="paperclip" />
                <p>
                  {t("v8_attachments", "Attachments")} {attachments.length > 0 ? `(${attachments.length})` : null}
                </p>
              </div>
            </MenuItem>
            <MenuItem name={INTERVENTION_TABS.Checklists} active={activeTab === INTERVENTION_TABS.Checklists} onClick={() => handleTab(INTERVENTION_TABS.Checklists)}>
              <div
                className={`tab ${pendingChecklistsCount === 0 ? "gray" : pendingChecklistsCount > 0 ? "red" : "green"} ${activeTab === INTERVENTION_TABS.Checklists ? "active" : ""}`}
              >
                <Icon className="standard list" />
                <p>
                  {t("v8_checklists", "Checklists")} {`${checksAndPendginChecklistsCount > 0 ? `(${checksAndPendginChecklistsCount})` : ""}`}
                </p>
              </div>
            </MenuItem>
          </Menu>
          <div className="tabs-container">
            {activeTab === INTERVENTION_TABS.General && <GeneralInfo intervention={intervention} appointment={appointment} typeID={pinTypeID} />}
            {activeTab === INTERVENTION_TABS.Subitems && <Subitems intervention={intervention} />}
            {selectedPinTypeOption && activeTab === INTERVENTION_TABS.PinType && (
              <PinCategory latestPinData={latestPin} intervention={intervention} selectedPinTypeOption={selectedPinTypeOption} appointment={appointment} />
            )}
            {activeTab === INTERVENTION_TABS.Attachments && <Attachments attachments={attachments} />}
            {activeTab === INTERVENTION_TABS.Checklists && <InterventionChecklists connectedChecks={connectedChecks} intervention={intervention} />}
          </div>
        </div>
      )}
    </div>
  );
};
